/**
 *  Copyright AlpineReplay Inc, 2021. All rights reserved.
 *  Authors: Brian Bal, Leonid Khramov
 */
import React, { useState, useEffect } from "react"
import { connect } from "react-redux"
import { withRouter } from "react-router"

import "./StaffContainer.css"
import { webApi } from "../../../lib/WebApi"
import * as act from "../actions/staff"
import StaffRow from "../components/StaffRow"
import { PRIVACY_POLICY, TERMS_OF_USE } from "../../../util/links"
import ContainerWithMenu from "./ContainerWithMenu"
import { validateEmail } from "../../../util/validator"
import { useAddMemberPhoneNumberMutation } from "../../../generated/graphql"

import TRCButton from "../../../trace/TRCButton"
import {
    pageTitle,
    Modal,
    ModalTitle,
    TitleSection,
    ContentSection,
    FooterSection,
    Button,
    Label,
    Input,
    Row,
    Column,
    Element,
    Color,
    useToken,
    Toaster,
} from "trux"
// import { validate } from "graphql"

class Staff extends ContainerWithMenu {
    constructor(props) {
        super(props, "Staff", "menu_staff")

        this.state = {
            isEditModalOpened: false,
            isDeleteModalOpened: false,
            feedbackClass: "none",
            feedbackText: "",
            isInviteModalOpened: false,
            inviteText: "",
            inviteError: null,
            inviteCallback: null,
            toast: null,
        }
        this.onRequestInviteModalClose =
            this.onRequestInviteModalClose.bind(this)
        this.openInviteModalDialog = this.openInviteModalDialog.bind(this)
        this.onInviteTextChanged = this.onInviteTextChanged.bind(this)
        this.onSubmitInviteForm = this.onSubmitInviteForm.bind(this)
        this.setInviteCallback = this.setInviteCallback.bind(this)
        this.addCoaches = this.addCoaches.bind(this)
    }

    componentDidMount() {
        pageTitle(this.props.title, "Settings - Staff")

        if (this.props.match.params.user && this.props.match.params.token) {
            this.props.fromIOS()
            var that = this
            this.props.loginByCode(
                this.props.match.params.user,
                this.props.match.params.token,
                () => {
                    that.props.loadStaff(this.props.divisionId)
                }
            )
        } else {
            this.props.loadStaff(this.props.divisionId)
        }
    }

    isFromIOS() {
        return this.props.via === "iosapp"
    }

    setInviteCallback(callback) {
        this.setState({ inviteCallback: callback })
    }

    showToast(message) {
        this.setState({ toast: message })
        setTimeout(() => {
            this.setState({ toast: null })
        }, 3000)
    }

    renderContent() {
        let division = this.props.divisions
            ? this.props.divisions.find(
                  (item) => item.division_id === this.props.divisionId
              )
            : {}
        if (!division) division = {}

        let staffRows = this.props.users.map((user) => {
            return (
                <StaffRow
                    location={this.props.location}
                    key={`staff-${user.user_id}`}
                    user={user}
                    currentUser={this.props.currentUser}
                />
            )
        })

        return (
            <Element className="StaffPage modal-container content">
                <Element className="trc-grid bottom-buttons">
                    <Element className="col ml-push-3 ml-ff6 ml-after-3 m-ff12">
                        <TRCButton full onClick={this.openInviteModalDialog}>
                            Invite
                        </TRCButton>
                    </Element>
                </Element>
                <Element className="StaffHeader trc-grid">
                    <Element className="col m-f2">Name</Element>
                    <Element className="col m-f3">Email</Element>
                    <Element className="col m-f1">Status</Element>
                </Element>
                {staffRows}
                <InviteModal
                    open={this.state.isInviteModalOpened}
                    onClose={this.onRequestInviteModalClose}
                    onSubmit={this.onSubmitInviteForm}
                    divisionTitle={division.title}
                    inviteError={this.state.inviteError}
                    setInviteCallback={this.setInviteCallback}
                />

                {this.state.toast && <Toaster message={this.state.toast} />}
            </Element>
        )
    }

    onRequestInviteModalClose(e) {
        if (e) e.preventDefault()
        this.setState({
            isInviteModalOpened: false,
        })
    }

    openInviteModalDialog() {
        this.setState({
            isInviteModalOpened: true,
            inviteText: "",
        })
    }

    onInviteTextChanged(e) {
        this.setState({ inviteText: e.target.value, inviteError: null })
    }

    onSubmitInviteForm(invites) {
        let emails = []
        for (let invite of invites) {
            emails.push(invite.email)
        }

        this.props.loadTeamsByDivision(this.props.divisionId, (teams) => {
            if (teams && teams.length < 3) {
                this.addCoaches(emails, teams)
            }
        })

        this.props.inviteStaff(this.props.divisionId, emails, (users) => {
            this.state.inviteCallback(users)
            this.showToast("Invites sent")
        })
        // hack to wait for dispatch to complete so this update will work
        setTimeout(() => {
            this.props.refreshStaff(this.props.divisionId)
        }, 1000)
        return false
    }

    addCoaches(emails, teams) {
        for (let email of emails) {
            this.props.addAsCoach(teams[0].team_id, email, (error) => {
                console.log('Adding team member as a coach failed: ', error)
            })

            if (teams[1]) {
                this.props.addAsCoach(teams[1].team_id, email, (error) => {
                    console.log('Adding team member as a coach failed: ', error)
                })
            }
        }
    }
}

const mapStateToProps = (state, props) => {
    const via = state.via
    const loadTeamsInProgress = !state.divisions.complete
    const loadTeamsError = state.divisions.error
    const users = state.division_staff.value.staff.sort((a, b) => {
        let an = `${a.first_name} ${a.last_name}`
        let bn = `${b.first_name} ${b.last_name}`
        return an.localeCompare(bn)
    })
    const currentUser = state.user.value
    const divisionId = parseInt(props.match.params.division_id, 10)
    let divisions = []

    // divisions
    if (state.divisions.value) {
        divisions = state.divisions.value.filter((div) => div.division_id > 0)
    }

    let nProps = {
        via,
        loadTeamsInProgress,
        loadTeamsError,
        users,
        currentUser,
        divisionId,
        divisions,
        title: "Members",
        now: Date.now(),
    }
    console.log("StaffContainer", "mapStateToProps", nProps)

    return nProps
}

const mapDispatchToProps = (dispatch) => {
    return {
        fromIOS: () => {
            dispatch({
                type: "COMMON_SET",
                key: "via",
                value: "iosapp",
            })
        },
        loginByCode: (userId, code, cb) => {
            webApi
                .loginByCode(userId, code)
                .then(() => {
                    cb(true)
                    dispatch({ type: "COMMON_RESET" })
                    dispatch({
                        type: "COMMON_SET",
                        key: "via",
                        value: "iosapp",
                    })
                })
                .catch((err) => {
                    console.warn("Admin.HomePage.loginByCode.ERROR", err)
                    cb(false)
                })
        },
        loadStaff: (divisionId) => {
            dispatch(act.loadStaffByDivision(divisionId))
        },
        inviteStaff: (divisionId, emails, callback) => {
            dispatch(act.inviteStaff(divisionId, emails, callback))
        },
        addAsCoach: (teamId, email, callback) => {
            dispatch(act.addAsCoachRequest(teamId, email, callback))
        },
        loadTeamsByDivision: (divisionId, callback) => {
            dispatch(act.loadTeamsRequestByDivision(divisionId, callback))
        },
        refreshStaff: (divisionId) => {
            dispatch(act.loadStaff(divisionId))
        },
    }
}

const StaffContainer = withRouter(
    connect(mapStateToProps, mapDispatchToProps)(Staff)
)

const InviteModal = ({
    open,
    onClose,
    onSubmit,
    divisionTitle,
    inviteError,
    setInviteCallback,
}) => {
    const [invites, setInvites] = useState([
        {
            email: "",
            phone: "",
            emailError: "",
            phoneError: "",
        },
        {
            email: "",
            phone: "",
            emailError: "",
            phoneError: "",
        },
        {
            email: "",
            phone: "",
            emailError: "",
            phoneError: "",
        },
    ])

    const token = useToken()
    const [addMemberPhoneNumberMutation] = useAddMemberPhoneNumberMutation()

    const onEmailChanged = (val, index) => {
        let updatedInvites = [...invites]
        updatedInvites[index].email = val
        setInvites(updatedInvites)
        console.log("onEmailChanged", val, index)
    }

    const onPhoneChanged = (val, index) => {
        let updatedInvites = [...invites]
        updatedInvites[index].phone = val
        setInvites(updatedInvites)
        console.log("onPhoneChanged", val, index)
    }

    const onEmailBlur = (index) => {
        let updatedInvites = [...invites]
        if (updatedInvites[index].email.length > 0) {
            if (!validateEmail(updatedInvites[index].email)) {
                updatedInvites[index].emailError = "Invalid email"
            } else {
                updatedInvites[index].emailError = ""
            }
        } else {
            updatedInvites[index].emailError = ""
        }
        setInvites(updatedInvites)
        console.log("onEmailBlur", index)
    }

    const onPhoneBlur = (index) => {
        let updatedInvites = [...invites]
        if (updatedInvites[index].phone.length > 0) {
            let cleanNumber = cleanPhoneNumber(updatedInvites[index].phone)
            if (!cleanNumber.startsWith("+")) {
                cleanNumber = "+1" + cleanNumber
            }
            if (!validatePhoneNumber(cleanNumber)) {
                updatedInvites[index].phoneError = "Invalid phone"
            } else {
                updatedInvites[index].phoneError = ""
            }
        } else {
            updatedInvites[index].phoneError = ""
        }
        setInvites(updatedInvites)
        console.log("onPhoneBlur", index)
    }

    const addRow = (e) => {
        if (e) {
            e.preventDefault()
        }
        let updatedInvites = [...invites]
        updatedInvites.push({
            email: "",
            phone: "",
            emailError: "",
            phoneError: "",
        })
        setInvites(updatedInvites)
    }

    const isRepeatEmail = (idx, email) => {
        let repeat = false
        for (let [index, invite] of invites.entries()) {
            // Skip the current index
            if (index >= idx) continue
            // Check the other entries (excluding the current index)
            if (invite.email === email) {
                repeat = true
                break
            }
        }
        return repeat
    }

    const validateAndSubmit = (e) => {
        if (e) {
            e.preventDefault()
        }
        let updatedInvites = []
        let allValid = true
        for (let [idx, invite] of invites.entries()) {
            if (invite.email.length > 0 || invite.phone.length > 0) {
                // Clean the phone number
                let cleanNumber = cleanPhoneNumber(invite.phone)
                if (!cleanNumber.startsWith("+")) {
                    cleanNumber = "+1" + cleanNumber
                }
                // Are individual phone and emails valid?
                const isPhoneValid = validatePhoneNumber(cleanNumber)
                const isEmailValid = validateEmail(invite.email)
                if (!isPhoneValid) {
                    let phoneError = !cleanNumber || cleanNumber.length < 1 || cleanNumber === "+1" ? "Phone required" : "Invalid phone"
                    invite.phoneError = phoneError
                    allValid = false
                }
                if (!isEmailValid) {
                    let emailError = !invite.email || invite.email.length < 1 ? "Email required" : "Invalid email"
                    invite.emailError = emailError
                    allValid = false
                }
                // Are there any repeated emails?
                const repeatEmail = isRepeatEmail(idx, invite.email)
                if (repeatEmail && isEmailValid) {
                    // If there's a repeat email, warn the user and don't submit
                    invite.emailError = "Duplicate email"
                    allValid = false
                }
            }
            updatedInvites.push(invite)
        }
        if (allValid) {
            let nonEmptyInvites = updatedInvites.filter((inv) => inv.email.length > 0 || inv.phone.length > 0)
            if (nonEmptyInvites && nonEmptyInvites.length === 0) {
                updatedInvites[0].emailError = "Email required"
                updatedInvites[0].phoneError = "Phone required"
                setInvites(updatedInvites)
            } else {
                onSubmit(nonEmptyInvites)
            }
        } else {
            setInvites(updatedInvites)
        }
    }

    const handleInviteCallback = (users) => {
        for (let invite of invites) {
            for (let user of users) {
                if (invite.email === user.email) {
                    // add phone number to user
                    let cleanNumber = cleanPhoneNumber(invite.phone)
                    if (!cleanNumber.startsWith("+")) {
                        cleanNumber = "+1" + cleanNumber
                    }
                    console.log(
                        "adding phone number",
                        user.email,
                        cleanNumber,
                        user.user_id,
                        user
                    )
                    addMemberPhoneNumberMutation({
                        variables: {
                            userId: user.user_id,
                            phoneNumber: cleanNumber,
                            token: token,
                        },
                    })
                }
            }
        }
        setInvites([
            {
                email: "",
                phone: "",
                emailError: "",
                phoneError: "",
            },
            {
                email: "",
                phone: "",
                emailError: "",
                phoneError: "",
            },
            {
                email: "",
                phone: "",
                emailError: "",
                phoneError: "",
            },
        ])
        onClose()
    }

    useEffect(() => {
        if (setInviteCallback) {
            setInviteCallback(handleInviteCallback)
        }
    }, [setInviteCallback, token, invites])

    return (
        <Modal open={open} onShadeClick={onClose}>
            <TitleSection onCloseClick={onClose}>
                <ModalTitle content="Invite" />
            </TitleSection>
            <ContentSection>
                <form id="add_staff_form" onSubmit={validateAndSubmit}>
                    <Label marginBottom="2">
                        Invite your coaching staff and/or team managers to&nbsp;
                        {divisionTitle}.
                    </Label>
                    <Label>
                        Everyone you invite will have the ability to Trace their
                        own game by downloading the Trace Teams App.
                    </Label>
                    <Label
                        marginBottom="4"
                        type={Label.Type.Heading4}
                        className={inviteError ? "error" : ""}
                    >
                        {inviteError ? inviteError : ""}
                    </Label>
                    {invites.map((invite, index) => {
                        return (
                            <Row key={`invite-${index}`} marginBottom="4">
                                <Input
                                    type={Input.Type.Email}
                                    label="Email"
                                    value={invite.email}
                                    error={invite.emailError}
                                    onChange={(val) => onEmailChanged(val, index)}
                                    required
                                    marginRight="4"
                                    onBlur={() => onEmailBlur(index)}
                                />
                                <Input
                                    type={Input.Type.Phone}
                                    label="Phone Number"
                                    value={invite.phone}
                                    error={invite.phoneError}
                                    onChange={(val) => onPhoneChanged(val, index)}
                                    required
                                    onBlur={() => onPhoneBlur(index)}
                                />
                            </Row>
                        )
                    })}
                    <Label onClick={addRow} touchable color={Color.Trace} content={"Add person"} marginBottom={1} />
                </form>
            </ContentSection>
            <FooterSection>
                <Column>
                    <Row>
                        <Button
                            content="Send Invites"
                            span="12"
                            primary
                            onClick={validateAndSubmit}
                            maringRight="4"
                        />
                        <Button
                            marginLeft="4"
                            content="Cancel"
                            span="12"
                            onClick={onClose}
                        />
                    </Row>
                    <Row padding="4">
                        <Element className="subscript">
                            By clicking &ldquo;Send Invites&rdquo;, you agree to
                            our <a href={TERMS_OF_USE}>Terms</a> and that you
                            have read our{" "}
                            <a href={PRIVACY_POLICY}>Privacy Policy</a>
                        </Element>
                    </Row>
                </Column>
            </FooterSection>
        </Modal>
    )
}

const cleanPhoneNumber = (phoneNumber) => {
    let num = phoneNumber.replace(/[^\d+]/g, "")
    return num
}

const validatePhoneNumber = (phoneNumber) => {
    let num = cleanPhoneNumber(phoneNumber)
    // use regex to validate phone number
    const regex = /^\+\d{11,20}/
    if (!regex.test(num)) {
        return false
    }
    return true
}

export default StaffContainer
